import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { ArrowLeft, AlertCircle, Link2, Mail, MessageSquarePlus, PlusCircle, Send } from 'lucide-react'
import {
  apiGetSupplierDetail,
  apiGetSupplierCommunications,
  apiSearchCatalogByEmail,
  apiLinkCampaignSupplierToCatalog,
  apiCreateSupplier,
  isApiError,
  SupplierDetailData,
  CommunicationLogEntry,
  CampaignStatus,
} from '@/lib/api'
import { SupplierStatusBadge } from './SupplierStatusBadge'
import { SupplierComplementRequest } from './suppliers/SupplierComplementRequest'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { cn } from '@/lib/utils'

const COMPLEMENT_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set(['SUBMITTED', 'RESUBMITTED'])
const ORPHAN_BANNER_STATUSES: ReadonlySet<string> = new Set(['SUBMITTED', 'RESUBMITTED'])

type Props = {
  campaignId: string
  campaignName: string
  supplierId: string
  supplierName: string
  onBack: () => void
  // Story 3.6 — quand fourni (arrivée via deep-link depuis le profil
  // fournisseur), le bouton retour appelle ce handler et porte un libellé
  // adapté. Sinon, comportement classique (`onBack`, retour au dashboard).
  onBackToProfile?: () => void
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100)
  const cls =
    confidence >= 0.85
      ? 'text-green-700 bg-green-100'
      : confidence >= 0.6
      ? 'text-amber-700 bg-amber-100'
      : 'text-red-700 bg-red-100'
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', cls)}>
      IA {pct}%
    </span>
  )
}

function DetailSkeleton({ name }: { name: string }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
        <div>
          <h2 className="text-xl font-bold">{name}</h2>
          <div className="h-3 w-32 bg-gray-200 rounded animate-pulse mt-1" />
        </div>
      </div>
      <div className="h-16 bg-gray-200 rounded animate-pulse" />
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="h-14 bg-gray-200 rounded animate-pulse" />
      ))}
    </div>
  )
}

export function SupplierDetailView({
  campaignId,
  campaignName,
  supplierId,
  supplierName,
  onBack,
  onBackToProfile,
}: Props) {
  const backHandler = onBackToProfile ?? onBack
  const backLabel = onBackToProfile
    ? 'Retour au profil fournisseur'
    : 'Retour au tableau de bord'
  const [data, setData] = useState<SupplierDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [communications, setCommunications] = useState<CommunicationLogEntry[]>([])
  const [commLoading, setCommLoading] = useState(true)
  const [complementOpen, setComplementOpen] = useState(false)

  const loadData = useCallback(() => {
    setLoading(true)
    setError(false)
    apiGetSupplierDetail(campaignId, supplierId)
      .then(setData)
      .catch(() => {
        setError(true)
        toast.error('Erreur lors du chargement du détail fournisseur', { duration: Infinity })
      })
      .finally(() => setLoading(false))
  }, [campaignId, supplierId])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    apiGetSupplierCommunications(campaignId, supplierId)
      .then(setCommunications)
      .catch(() => {/* silencieux — historique non critique */})
      .finally(() => setCommLoading(false))
  }, [campaignId, supplierId])

  if (loading) return <DetailSkeleton name={supplierName} />

  if (error || !data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={backHandler}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {backLabel}
        </Button>
        <p className="text-center py-8 text-destructive">
          Impossible de charger les données.{' '}
          <button className="underline" onClick={loadData}>Réessayer</button>
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-4 flex-wrap">
        <Button variant="ghost" size="sm" onClick={backHandler}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {backLabel}
        </Button>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-bold">{data.supplier.name}</h2>
            {/* Story 3.8 — badge "Ajouté en cours de campagne" pour les
                fournisseurs ajoutés après le lancement (post-launch). */}
            {data.supplier.addedPostLaunch && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 rounded px-1.5 py-0.5"
                    >
                      <PlusCircle className="h-3 w-3" aria-hidden />
                      Ajouté en cours de campagne
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Ajouté le {new Date(data.supplier.assignedAt).toLocaleDateString('fr-FR')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{data.supplier.email}</p>
        </div>
        {data.survey && <SupplierStatusBadge status={data.survey.uxStatus} />}

        {/* Story 3.1 follow-up — CTA « Demander un complément » dans la vue campagne.
            Désactivé si statut non éligible OU fournisseur hors catalogue OU campagne fermée. */}
        <ComplementCTA
          submissionStatus={data.supplier.submissionStatus}
          catalogSupplierId={data.supplier.catalogSupplierId}
          campaignStatus={data.campaign.status}
          onClick={() => setComplementOpen(true)}
        />
      </div>

      {data.supplier.catalogSupplierId && (
        <SupplierComplementRequest
          supplierId={data.supplier.catalogSupplierId}
          campaignSupplierId={data.supplier.id}
          campaignName={campaignName}
          sections={data.template.sections}
          open={complementOpen}
          onOpenChange={setComplementOpen}
          onSuccess={() => {
            setComplementOpen(false)
            loadData()
          }}
        />
      )}

      {/* Story 3.5 Volet D — banner d'avertissement si fournisseur hors catalogue
          ET déjà soumis : le CTA complément est inaccessible tant que la liaison
          n'est pas faite. Lancer la mini-modale `LinkToCatalogModal`. */}
      {data.supplier.catalogSupplierId === null
        && ORPHAN_BANNER_STATUSES.has(data.supplier.submissionStatus) && (
        <OrphanCatalogBanner
          campaignId={campaignId}
          campaignSupplierId={data.supplier.id}
          supplierName={data.supplier.name}
          supplierEmail={data.supplier.email}
          onLinked={loadData}
        />
      )}

      {/* Indicateur de progression */}
      <div className="rounded-md border p-4 bg-muted/30">
        <p className="font-semibold">
          {data.stats.requiredFilled} / {data.stats.requiredTotal} champs obligatoires remplis
        </p>
        {data.stats.optionalTotal > 0 && (
          <p className="text-sm text-muted-foreground mt-1">
            {data.stats.optionalFilled} / {data.stats.optionalTotal} champs optionnels remplis
          </p>
        )}
      </div>

      {/* Sections et champs */}
      {data.template.sections.length === 0 && (
        <p className="text-center py-8 text-muted-foreground">Aucune section définie dans ce template.</p>
      )}
      {data.template.sections.map((section: any) => (
        <div key={section.id} className="space-y-3">
          <h3 className="font-semibold border-b pb-2">{section.title}</h3>
          {(section.fields ?? []).map((field: any) => {
            const value = data.answers[field.id]
            const hasValue = value !== null && value !== undefined && value !== ''
            const extraction = data.extractions.find(e => e.fieldId === field.id)
            const isMissingRequired = field.required && !hasValue

            return (
              <div
                key={field.id}
                className={cn(
                  'rounded-md border p-3',
                  isMissingRequired && 'bg-red-50 border-red-200'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{field.label}</p>
                      {field.required ? (
                        <Badge variant="outline" className="text-xs border-red-300 text-red-700">
                          Obligatoire
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          Optionnel
                        </Badge>
                      )}
                      {field.unit && (
                        <span className="text-xs text-muted-foreground">({field.unit})</span>
                      )}
                      {extraction && hasValue && <ConfidenceBadge confidence={extraction.confidence} />}
                    </div>
                    <p className={cn('text-sm mt-1', hasValue ? '' : 'text-muted-foreground italic')}>
                      {hasValue ? String(value) : 'Non renseigné'}
                    </p>
                    {extraction && hasValue && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Source IA : {extraction.documentUrl}
                      </p>
                    )}
                  </div>
                  {isMissingRequired && (
                    <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ))}

      {/* Historique des communications */}
      <div className="space-y-3">
        <h3 className="font-semibold border-b pb-2">Historique des communications</h3>
        {commLoading ? (
          <div className="space-y-2">
            {[1, 2].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        ) : communications.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Aucune communication enregistrée.</p>
        ) : (
          <ul className="space-y-2">
            {communications.map(log => (
              <li key={log.id} className="flex items-start gap-3 rounded-md border p-3">
                {log.type === 'invitation' ? (
                  <Mail className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Send className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{log.subject}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {log.type === 'invitation' ? 'Invitation initiale' : `Relance${log.sentByName ? ` par ${log.sentByName}` : ''}`}
                    {' · '}
                    {new Intl.DateTimeFormat('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    }).format(new Date(log.sentAt))}
                  </p>
                </div>
                {log.status === 'failed' && (
                  <span className="text-xs text-red-500 flex-shrink-0">Échec</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// ─── Story 3.5 Volet D — banner + mini-modale Lier au catalogue ─────────────

function OrphanCatalogBanner({
  campaignId,
  campaignSupplierId,
  supplierName,
  supplierEmail,
  onLinked,
}: {
  campaignId: string
  campaignSupplierId: string
  supplierName: string
  supplierEmail: string
  onLinked: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <div
        role="alert"
        className="rounded-md border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 flex-wrap"
      >
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-[240px]">
          <p className="text-sm font-medium text-amber-900">
            Ce fournisseur n'est pas lié au catalogue
          </p>
          <p className="text-xs text-amber-800 mt-1">
            Action « Demander un complément » indisponible. Liez ce fournisseur à votre catalogue
            pour débloquer les actions cross-campagne.
          </p>
        </div>
        <Button
          size="sm"
          variant="default"
          onClick={() => setOpen(true)}
          className="bg-amber-700 hover:bg-amber-800 text-white"
        >
          <Link2 className="mr-2 h-4 w-4" />
          Lier maintenant
        </Button>
      </div>

      <LinkToCatalogModal
        open={open}
        onOpenChange={setOpen}
        campaignId={campaignId}
        campaignSupplierId={campaignSupplierId}
        supplierName={supplierName}
        supplierEmail={supplierEmail}
        onLinked={() => {
          setOpen(false)
          onLinked()
        }}
      />
    </>
  )
}

function LinkToCatalogModal({
  open,
  onOpenChange,
  campaignId,
  campaignSupplierId,
  supplierName,
  supplierEmail,
  onLinked,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: string
  campaignSupplierId: string
  supplierName: string
  supplierEmail: string
  onLinked: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [match, setMatch] = useState<{ id: string; name: string; email: string } | null>(null)
  const [searchDone, setSearchDone] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Helper exposé pour le bouton « Réessayer » (cas erreur réseau) et pour le
  // fallback 409 sur create-and-link (un autre acteur a créé le supplier entre
  // notre lookup initial et notre POST). Renvoie le match s'il existe, sinon null.
  const searchCatalog = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setLoading(true)
      setSearchError(null)
      try {
        const res = await apiSearchCatalogByEmail(supplierEmail)
        if (signal?.cancelled) return null
        setMatch(res.match)
        setSearchDone(true)
        return res.match
      } catch (e: unknown) {
        if (signal?.cancelled) return null
        const msg = e instanceof Error ? e.message : 'Erreur lors de la recherche dans le catalogue'
        setSearchError(msg)
        setMatch(null)
        setSearchDone(true)
        return null
      } finally {
        if (!signal?.cancelled) setLoading(false)
      }
    },
    [supplierEmail]
  )

  useEffect(() => {
    if (!open) {
      setMatch(null)
      setSearchDone(false)
      setSearchError(null)
      return
    }
    // Garde anti-race : si le modal se ferme/rouvre avant que la promesse résolve,
    // on annule l'écriture du state (évite d'afficher un match obsolète).
    const signal = { cancelled: false }
    void searchCatalog(signal)
    return () => {
      signal.cancelled = true
    }
  }, [open, searchCatalog])

  async function handleLink(catalogSupplierId: string) {
    setBusy(true)
    try {
      await apiLinkCampaignSupplierToCatalog(campaignId, campaignSupplierId, catalogSupplierId)
      toast.success('Fournisseur lié au catalogue', { duration: 4000 })
      onLinked()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erreur lors du lien au catalogue'
      toast.error(msg, { duration: Infinity })
    } finally {
      setBusy(false)
    }
  }

  async function handleCreateAndLink() {
    setBusy(true)
    try {
      const created = await apiCreateSupplier({ name: supplierName, email: supplierEmail })
      await apiLinkCampaignSupplierToCatalog(campaignId, campaignSupplierId, created.id)
      toast.success('Fournisseur créé dans le catalogue et lié', { duration: 4000 })
      onLinked()
    } catch (e: unknown) {
      // Fallback 409 : un autre utilisateur (ou un autre onglet) a créé le
      // supplier entre notre lookup initial et ce POST. Plutôt que d'afficher
      // une erreur opaque, on re-query le catalogue pour proposer le link
      // direct au fournisseur qui vient d'apparaître.
      if (isApiError(e) && e.status === 409) {
        const refreshedMatch = await searchCatalog()
        if (refreshedMatch) {
          toast.info('Un fournisseur avec cet email vient d\'apparaître dans votre catalogue. Vous pouvez le lier directement.', {
            duration: 8000,
          })
          setBusy(false)
          return
        }
      }
      const msg = e instanceof Error ? e.message : 'Erreur lors de la création dans le catalogue'
      toast.error(msg, { duration: Infinity })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Lier au catalogue</DialogTitle>
          <DialogDescription>
            Lier <strong>{supplierName}</strong> ({supplierEmail}) à un fournisseur de votre catalogue.
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-muted-foreground">Recherche dans le catalogue...</p>}

        {!loading && searchDone && searchError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 space-y-2">
            <p className="text-sm text-destructive">
              Erreur lors de la recherche : {searchError}
            </p>
            <Button size="sm" variant="outline" onClick={() => void searchCatalog()} disabled={busy}>
              Réessayer
            </Button>
          </div>
        )}

        {!loading && searchDone && !searchError && match && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <p className="text-sm">
              Match trouvé : <strong>{match.name}</strong>
            </p>
            <p className="text-xs text-muted-foreground">{match.email}</p>
            <Button
              size="sm"
              onClick={() => handleLink(match.id)}
              disabled={busy}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Lier à ce fournisseur
            </Button>
          </div>
        )}

        {!loading && searchDone && !searchError && !match && (
          <div className="rounded-md border bg-muted/30 p-3 space-y-2">
            <p className="text-sm">
              Aucun fournisseur ne correspond à <strong>{supplierEmail}</strong> dans votre catalogue.
            </p>
            <Button size="sm" onClick={handleCreateAndLink} disabled={busy}>
              Créer dans le catalogue et lier
            </Button>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={busy}>
            Annuler
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ComplementCTA({
  submissionStatus,
  catalogSupplierId,
  campaignStatus,
  onClick,
}: {
  submissionStatus: string
  catalogSupplierId: string | null
  // Story 3.7 AC #6 — accepter l'union complète `CampaignStatus`. Le gate
  // `campaignOpen = campaignStatus === 'ACTIVE'` reste explicite.
  campaignStatus: CampaignStatus
  onClick: () => void
}) {
  const eligibleStatus = COMPLEMENT_ELIGIBLE_STATUSES.has(submissionStatus)
  const linkedToCatalog = catalogSupplierId !== null
  const campaignOpen = campaignStatus === 'ACTIVE'
  const enabled = eligibleStatus && linkedToCatalog && campaignOpen

  const tooltip = !campaignOpen
    ? 'La campagne est fermée. Réouvrez-la pour pouvoir demander un complément.'
    : !eligibleStatus
      ? 'Disponible uniquement après soumission (SUBMITTED ou RESUBMITTED).'
      : !linkedToCatalog
        ? 'Ce fournisseur n\'est pas lié au catalogue. Reliez-le depuis Fournisseurs pour pouvoir cibler des champs.'
        : undefined

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={!enabled}
      title={tooltip}
      className="ml-auto"
    >
      <MessageSquarePlus className="mr-2 h-4 w-4" />
      Demander un complément
    </Button>
  )
}
